/**
 * 撮影の流れ（タブレット。Issue #711。索引 #709）: サーバーの対戦の状態（GetLiveMatch）を席で見て、撮影の問い合わせ（試合を選ぶ・録画開始・録画を停止）に
 * 答える（AnswerLiveMatch）。入力画面の席モード（?seat=1|2）の上に出す。
 * 録画を始める・止めるのは OBS / TetherCam が自分で（補正サーバー経由。#714）。状態が進むのは録画の状態がサーバーに届いてから
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/api';

export const MATCH_POLL_MS = 1500;
const STATE_TEXT = { idle: '対戦アイドル', ready: '対戦待機中', playing: '対戦中', finished: '対戦終了' };

export function matchSummary(m) {
  if (!m) return '撮影の状態を読んでいます…';
  const g = m.game ? [m.game.duel_name, m.game.game_name].filter(Boolean).join(' ') : null;
  return [STATE_TEXT[m.state] ?? m.state, g, m.mode === 'remote' ? 'リモート' : m.mode === 'local' ? '同席' : null].filter(Boolean).join('・');
}

/** seat は 0 / 1。onMatch(m) は状態が変わるたび */
export default function MatchPanel({ seat, compact = false, onMatch = null }) {
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const rev = useRef(null);
  const onMatchRef = useRef(onMatch); onMatchRef.current = onMatch;

  const refresh = async () => {
    try {
      const r = await api.project('GetLiveMatch', { seat, ...(rev.current != null ? { since_rev: rev.current } : {}) });
      setError(null);
      if (r?.changed === false) return;
      rev.current = r?.rev ?? null;
      setM(r);
      onMatchRef.current?.(r);
    } catch (e) { setError(`撮影の状態を読めません: ${e.message ?? e}`); }
  };
  useEffect(() => {
    rev.current = null;
    let alive = true;
    const tick = () => { if (alive && !document.hidden) refresh(); };
    tick();
    const t = setInterval(tick, MATCH_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [seat]);   // eslint-disable-line react-hooks/exhaustive-deps

  const answer = async (opt) => {
    if (!m?.query) return;
    setBusy(true);
    setNote(null);
    try {
      const r = await api.project('AnswerLiveMatch', { seat, answer: { id: m.query.id, picks: [opt.index] } });
      const applied = r?.applied?.applied;
      if (applied === 'record_start' || applied === 'record_stop') {
        setNote(applied === 'record_start' ? '録画開始を伝えました。OBS / TetherCam が録画を始めると進みます' : '録画の停止を伝えました');
      }
      rev.current = null;
      await refresh();
    } catch (e) { setNote(`答えられませんでした: ${e.message ?? e}`); } finally { setBusy(false); }
  };

  const qy = m?.query;
  return (
    <div data-testid="match-panel" style={{ display: 'grid', gap: 6, padding: compact ? '6px 10px' : 0, fontSize: compact ? 13 : 14, background: compact ? '#1b2234' : undefined }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>撮影</strong>
        <span data-testid="match-summary">{matchSummary(m)}</span>
        {m?.dropped ? <span style={{ color: '#ff8a8a' }}>録画落ち: {m.dropped}</span> : null}
        {m?.notice ? <span role="status" style={{ color: '#ffb347' }}>{m.notice}</span> : null}
      </div>
      {qy && qy.kind !== 'match_wait' ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{qy.message}</span>
          {(qy.options ?? []).map((o) => (
            <button key={o.index} type="button" className="btn" disabled={busy} onClick={() => answer(o)} data-testid={`match-option-${o.index}`}>{o.label}</button>
          ))}
        </div>
      ) : qy?.kind === 'match_wait' ? <div style={{ color: '#8a93a8' }}>{qy.message}</div> : null}
      {note ? <div style={{ fontSize: 12, color: '#b8c0d4' }}>{note}</div> : null}
      {error ? <div style={{ fontSize: 12, color: '#ff8a8a' }}>{error}</div> : null}
    </div>
  );
}
